import { useTranslations } from '@vocab/react';
import {
  Box,
  BraidPortal,
  Column,
  Columns,
  Stack,
  Text,
} from 'braid-design-system';
import type React from 'react';
import { type PropsWithChildren, useContext, useEffect } from 'react';
import { usePopperTooltip } from 'react-popper-tooltip';

import translations from '@seek/rr-caja/src/.vocab';

import { ToolTipName } from '../../analytics/types';
import { ForwardedBlankButton } from '../BlankButton/BlankButton';
import { IconSeekPassInvert } from '../Icons';
import { AnalyticsContext } from '../RoleRequirements/RoleRequirements';

import * as style from '../ContextualHelp/ContextualHelp.css';

const CertsyTooltip: React.FunctionComponent<PropsWithChildren> = ({
  children,
}) => {
  const analytics = useContext(AnalyticsContext);
  const { t } = useTranslations(translations);

  const {
    getArrowProps,
    getTooltipProps,
    setTooltipRef,
    setTriggerRef,
    visible,
  } = usePopperTooltip({
    offset: [0, 8],
    placement: 'top',
  });

  useEffect(() => {
    if (visible) {
      analytics.sendContextualHelpLinkSelectedEvent(ToolTipName.Certsy);
    }
  }, [visible, analytics]);

  return (
    <span aria-haspopup="dialog">
      <ForwardedBlankButton
        style={{
          zIndex: 1,
          position: 'relative',
          display: 'inline',
          fontSize: '12px',
          top: '-1px',
          marginLeft: '2px',
        }}
        aria-label={t('aria-label:help-information')}
        ref={setTriggerRef}
      >
        {children}
      </ForwardedBlankButton>
      {visible && (
        <BraidPortal>
          <Box
            aria-modal={true}
            data-testid="tooltip"
            role="dialog"
            boxShadow="large"
            background="neutral"
            borderRadius="standard"
            maxWidth="xsmall"
            zIndex={2}
            ref={setTooltipRef}
            {...getTooltipProps()}
          >
            <Box padding="medium">
              <Stack space="medium">
                <Columns space="small" alignY="center">
                  <Column>
                    <Text weight="medium" size="standard">
                      {t('tooltip.certsy.title')}
                    </Text>
                  </Column>
                </Columns>
                <Text size="small">{t('tooltip.certsy.body1')}</Text>
                <Text size="small">{t('tooltip.certsy.body2')}</Text>
                <Columns space="xxsmall">
                  <Column width="content">
                    <Box marginRight="xxsmall">
                      <Text size="xsmall">
                        <IconSeekPassInvert />
                      </Text>
                    </Box>
                  </Column>
                  <Column>
                    <Text size="small">{t('tooltip.certsy.body3')}</Text>
                  </Column>
                </Columns>
              </Stack>
            </Box>
            <Box
              {...getArrowProps()}
              borderRadius="standard"
              background="neutral"
              className={style.arrow}
            />
          </Box>
        </BraidPortal>
      )}
    </span>
  );
};

export default CertsyTooltip;
